import React, { useEffect, useState } from 'react'
import { CheckCircle2, Clock, Crown, Loader2, RefreshCw, Search, Users, XCircle } from 'lucide-react'
import { API_CONFIG } from '../../config/api.config'

const API = API_CONFIG.python.apiUrl
const token = () => localStorage.getItem('jwt_token') || localStorage.getItem('auth_token') || localStorage.getItem('token') || ''

async function req<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`${API}${path}`, {
    ...init,
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token()}`, ...(init?.headers || {}) },
  })
  if (!res.ok) {
    const d = await res.json().catch(() => ({}))
    throw new Error(d.detail || `HTTP ${res.status}`)
  }
  return res.json()
}

interface GroupPayment {
  id: string
  amount_vnd: number
  seats: number
  memo: string
  status: string
  created_at?: string | null
  confirmed_at?: string | null
}

interface TeacherGroupRow {
  id: string
  name: string
  subject: string
  school: string
  join_code: string
  owner_id: string
  owner_name: string
  owner_email: string
  memberCount: number
  plan: 'free' | 'pro'
  isPro: boolean
  plan_until: string
  maxMembers: number
  suggested_price: number
  suggested_seats: number
  pending_payments: number
  latest_payment?: GroupPayment | null
  created_at?: string | null
}

const fmtVnd = (n: number) => (n || 0).toLocaleString('vi-VN') + 'đ'
const fmtDate = (s?: string | null) => s ? new Date(s).toLocaleDateString('vi-VN') : '—'

const TeacherGroupPlanManager: React.FC = () => {
  const [rows, setRows] = useState<TeacherGroupRow[]>([])
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [plan, setPlan] = useState('')
  const [busy, setBusy] = useState<string | null>(null)

  const load = async () => {
    setLoading(true)
    try {
      const data = await req<TeacherGroupRow[]>(`/teacher-group/admin/groups?search=${encodeURIComponent(search)}&plan=${encodeURIComponent(plan)}&limit=300`)
      setRows(data)
    } catch (e: any) {
      alert(e.message || 'Không tải được danh sách tổ')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { load() }, [])
  useEffect(() => { load() }, [plan])

  const grant = async (g: TeacherGroupRow) => {
    const seatsText = prompt('Số giáo viên tối đa của Gói Tổ:', String(Math.max(g.suggested_seats || 5, g.memberCount || 0, 5)))
    if (!seatsText) return
    const seats = Number(seatsText)
    if (!Number.isFinite(seats) || seats < Math.max(g.memberCount || 0, 1)) {
      alert('Số ghế không hợp lệ hoặc nhỏ hơn số thành viên hiện có.')
      return
    }
    const monthsText = prompt('Thời hạn gói, tính theo tháng:', '12')
    if (!monthsText) return
    const months = Number(monthsText)
    if (!Number.isFinite(months) || months <= 0) {
      alert('Số tháng không hợp lệ.')
      return
    }
    setBusy(g.id)
    try {
      await req('/teacher-group/admin/group-pro', {
        method: 'POST',
        body: JSON.stringify({ group_id: g.id, plan: 'pro', months, max_members: seats }),
      })
      await load()
    } catch (e: any) {
      alert(e.message || 'Cấp gói chưa thành công')
    } finally {
      setBusy(null)
    }
  }

  const revoke = async (g: TeacherGroupRow) => {
    if (!confirm(`Hủy Gói Tổ PRO của "${g.name}"?`)) return
    setBusy(g.id)
    try {
      await req('/teacher-group/admin/group-pro', {
        method: 'POST',
        body: JSON.stringify({ group_id: g.id, plan: 'free' }),
      })
      await load()
    } catch (e: any) {
      alert(e.message || 'Hủy gói chưa thành công')
    } finally {
      setBusy(null)
    }
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-black text-gray-900 flex items-center gap-2"><Crown className="text-amber-500" /> Duyệt Gói Tổ chuyên môn</h1>
          <p className="text-sm text-gray-500">Admin xem các tổ đã tạo, kiểm tra yêu cầu/thanh toán gần nhất và cấp hoặc hủy Gói Tổ PRO.</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <select value={plan} onChange={e => setPlan(e.target.value)} className="py-2 px-3 border border-gray-200 rounded-xl text-sm bg-white outline-none">
            <option value="">Tất cả trạng thái</option>
            <option value="free">Miễn phí</option>
            <option value="pro">PRO</option>
          </select>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
            <input value={search} onChange={e => setSearch(e.target.value)} onKeyDown={e => e.key === 'Enter' && load()}
              placeholder="Tìm tổ, môn, trường, mã…" className="pl-9 pr-3 py-2 border border-gray-200 rounded-xl text-sm w-64 outline-none focus:border-blue-400" />
          </div>
          <button onClick={load} className="p-2 text-gray-500 hover:text-blue-600" title="Tải lại"><RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} /></button>
        </div>
      </div>

      <div className="grid sm:grid-cols-3 gap-3 mb-4">
        <div className="bg-white rounded-2xl border border-gray-100 p-4">
          <div className="text-xs font-bold text-gray-400 uppercase">Tổng tổ đang xem</div>
          <div className="text-2xl font-black text-gray-900">{rows.length}</div>
        </div>
        <div className="bg-white rounded-2xl border border-emerald-100 p-4">
          <div className="text-xs font-bold text-gray-400 uppercase">Đang PRO</div>
          <div className="text-2xl font-black text-emerald-700">{rows.filter(r => r.isPro).length}</div>
        </div>
        <div className="bg-white rounded-2xl border border-amber-100 p-4">
          <div className="text-xs font-bold text-gray-400 uppercase">Có thanh toán chờ</div>
          <div className="text-2xl font-black text-amber-700">{rows.filter(r => (r.pending_payments || 0) > 0).length}</div>
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead className="bg-gray-50 text-[11px] uppercase tracking-wide text-gray-500 font-black">
            <tr>
              <th className="px-4 py-3 text-left">Tổ chuyên môn</th>
              <th className="px-4 py-3 text-left">Tổ trưởng</th>
              <th className="px-4 py-3 text-center">Thành viên</th>
              <th className="px-4 py-3 text-center">Gói</th>
              <th className="px-4 py-3 text-left">Thanh toán gần nhất</th>
              <th className="px-4 py-3 text-right">Thao tác</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {loading && rows.length === 0 ? (
              <tr><td colSpan={6} className="py-12 text-center text-blue-500"><Loader2 className="animate-spin mx-auto" /></td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan={6} className="py-12 text-center text-gray-400">Chưa có tổ phù hợp.</td></tr>
            ) : rows.map(g => (
              <tr key={g.id} className="hover:bg-gray-50/50">
                <td className="px-4 py-3 min-w-[240px]">
                  <div className="font-black text-gray-900">{g.name}</div>
                  <div className="text-xs text-gray-500">{[g.subject, g.school].filter(Boolean).join(' · ') || '—'}</div>
                  <div className="text-[11px] text-gray-400 font-mono mt-1">Mã: {g.join_code}</div>
                </td>
                <td className="px-4 py-3 min-w-[190px]">
                  <div className="font-bold text-gray-800">{g.owner_name || '—'}</div>
                  <div className="text-xs text-gray-400">{g.owner_email || '—'}</div>
                </td>
                <td className="px-4 py-3 text-center">
                  <div className="inline-flex items-center gap-1 font-bold text-gray-700"><Users className="w-4 h-4 text-blue-400" /> {g.memberCount}/{g.maxMembers}</div>
                  <div className="text-[11px] text-gray-400">Gợi ý {g.suggested_seats} ghế</div>
                </td>
                <td className="px-4 py-3 text-center">
                  {g.isPro ? (
                    <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-emerald-50 text-emerald-700 text-xs font-black"><CheckCircle2 className="w-3.5 h-3.5" /> PRO</span>
                  ) : (
                    <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-gray-100 text-gray-500 text-xs font-bold"><XCircle className="w-3.5 h-3.5" /> Free</span>
                  )}
                  <div className="text-[11px] text-gray-400 mt-1">Hạn: {fmtDate(g.plan_until)}</div>
                </td>
                <td className="px-4 py-3 min-w-[210px]">
                  {g.latest_payment ? (
                    <div>
                      <div className="font-bold text-gray-800">{fmtVnd(g.latest_payment.amount_vnd)} · {g.latest_payment.seats} ghế</div>
                      <div className="text-xs text-gray-500 font-mono">{g.latest_payment.memo}</div>
                      <div className={`text-[11px] font-bold inline-flex items-center gap-1 mt-1 ${(g.pending_payments || 0) > 0 ? 'text-amber-700' : 'text-emerald-700'}`}>
                        <Clock className="w-3 h-3" /> {g.latest_payment.status}{(g.pending_payments || 0) > 0 ? ` · ${g.pending_payments} chờ` : ''}
                      </div>
                    </div>
                  ) : (
                    <div className="text-xs text-gray-400">Chưa có thanh toán<br />Giá gợi ý: <b>{fmtVnd(g.suggested_price)}</b></div>
                  )}
                </td>
                <td className="px-4 py-3 text-right whitespace-nowrap">
                  <button disabled={busy === g.id} onClick={() => grant(g)}
                    className="px-3 py-1.5 rounded-lg bg-amber-500 text-white text-xs font-black hover:bg-amber-600 disabled:opacity-50">
                    {g.isPro ? 'Gia hạn/sửa' : 'Duyệt PRO'}
                  </button>
                  {g.isPro && (
                    <button disabled={busy === g.id} onClick={() => revoke(g)}
                      className="ml-2 px-3 py-1.5 rounded-lg border border-gray-200 text-gray-600 text-xs font-bold hover:bg-gray-50 disabled:opacity-50">
                      Hủy
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

export default TeacherGroupPlanManager